import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import type { CSSProperties, JSX, ReactNode } from 'react';
import React from 'react';

import type { TAnimationDirection } from '@feathr/extender/state';
import { NavigationProvider, useFlags, useNavigation } from '@feathr/extender/state';
import { useOverflowDetection } from '@feathr/hooks';

import AppLayoutV1 from './AppLayoutV1';
import CommunitySecondaryNav from './Page/CommunitySecondaryNav';
import HomeSecondaryNav from './Page/HomeSecondaryNav';
import PrimaryNav from './Page/PrimaryNav';
import NavBottomSection from './Page/PrimaryNav/NavBottomSection';
import ProjectsSecondaryNav from './Page/ProjectsSecondaryNav';
import SettingsSecondaryNav from './Page/SettingsSecondaryNav';

import * as styles from './AppLayout.css';

interface IAppLayoutProps {
  children: ReactNode;
}

function getSlideVariants(shouldReduceMotion: boolean | null) {
  const getOffset = (direction: TAnimationDirection, invert = false): string => {
    if (shouldReduceMotion) {
      return '0%';
    }
    const isRight = direction === 'right';
    if (invert) {
      return isRight ? '-100%' : '100%';
    }
    return isRight ? '100%' : '-100%';
  };

  return {
    enter: (direction: TAnimationDirection) => ({
      x: getOffset(direction),
      opacity: shouldReduceMotion ? 1 : 0,
    }),
    center: { x: 0, opacity: 1 },
    exit: (direction: TAnimationDirection) => ({
      x: getOffset(direction, true),
      opacity: shouldReduceMotion ? 1 : 0,
    }),
  };
}

function AppLayoutContent({ children }: Readonly<IAppLayoutProps>): JSX.Element {
  const { navWidth, activeSecondaryNav, animationDirection, setIsAnimating, scrollableRef } =
    useNavigation();
  const shouldReduceMotion = useReducedMotion();
  const { hasOverflow } = useOverflowDetection(scrollableRef);

  const style: CSSProperties = {
    '--l-nav-width': `${navWidth}px`,
  } as CSSProperties;

  const slideVariants = getSlideVariants(shouldReduceMotion);

  const renderNav = (): JSX.Element => {
    switch (activeSecondaryNav) {
      case 'home':
        return <HomeSecondaryNav />;

      case 'projects':
        return <ProjectsSecondaryNav />;

      case 'community':
        return <CommunitySecondaryNav />;

      case 'settings':
        return <SettingsSecondaryNav />;

      default:
        return <PrimaryNav />;
    }
  };

  return (
    <div className={styles.root} style={style}>
      {children}
      <div className={styles.sidebarWrapper}>
        <AnimatePresence
          custom={animationDirection}
          mode={'wait'}
          onExitComplete={(): void => setIsAnimating(false)}
        >
          <motion.div
            animate={'center'}
            className={styles.sidebar}
            custom={animationDirection}
            exit={'exit'}
            initial={'enter'}
            key={activeSecondaryNav ?? 'primary'}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            variants={slideVariants}
          >
            {renderNav()}
          </motion.div>
        </AnimatePresence>
        <NavBottomSection className={styles.navBottom} showShadow={hasOverflow} />
      </div>
    </div>
  );
}

function AppLayout({ children }: Readonly<IAppLayoutProps>): JSX.Element {
  const { showNewPrimaryNav } = useFlags();

  if (!showNewPrimaryNav) {
    return <AppLayoutV1>{children}</AppLayoutV1>;
  }

  return (
    <NavigationProvider>
      <AppLayoutContent>{children}</AppLayoutContent>
    </NavigationProvider>
  );
}

export default AppLayout;
